import { Col3Col9, RadioButton, RichTextEditor } from '@aon/aon-tws-common-ui';
import { IRadioOption } from '@aon/aon-tws-common-ui/build/lib/components/Common/RadioButton';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Constants from '../../../../constants';
import ContentConstants from '../../../../contentConstants';
import { EditState, IPageFlowElementModel, ITranslationEditItem } from '../../../../models';
import { SurveyDesignHelper } from '../../../../utilities';
import { PageFlowElementModalUtil } from '../../../../utilities/PageFlowElementModalUtil';
import { TranslationEditor } from '../../../translations/TranslationEditor';
import { PageFlowQuestionSection } from '../PageFlowQuestionSection';
import { IPageFlowElementModalProps, PageFlowElementModal } from './PageFlowElementModal';

const PositionContainer = styled.div.attrs<{}>({
	className: 'tws-position-container',
})`
	width: 80%;
`;
PositionContainer.displayName = 'PositionContainer';

const QuestionSelectContainer = styled.div.attrs<{}>({
	className: 'tws-question-select-container',
})`
	margin-top: -8px;
	width: 100%;
`;
QuestionSelectContainer.displayName = 'QuestionSelectContainer';

const BeforeQuestionContainer = styled.div.attrs<{}>({
	className: 'tws-before-question-container',
})`
	display: flex;
	align-items: top;
	margin-bottom: 18px;
`;
BeforeQuestionContainer.displayName = 'BeforeQuestionContainer';

const RadioButtonContainer = styled.div.attrs<{}>({
	className: 'tws-radio-bu-container',
})`
	width: 300px;
`;
RadioButtonContainer.displayName = 'RadioButtonContainer';

export const DescriptionTextModal = (props: IPageFlowElementModalProps) => {
	const InitializeRadios = () => {
		const radiosOptions: IRadioOption[] = [
			{
				name: 'beforeQuestions',
				displayText: ContentConstants.LOGIC_AND_LAYOUT.BEFORE_QUESTION,
				checked: true,
			},
			{
				name: 'endOfSurvey',
				displayText: ContentConstants.LOGIC_AND_LAYOUT.END_OF_SURVEY,
				checked: false,
			},
		];
		return radiosOptions;
	};

	const handleEffect = () => {
		setDescriptionTextError(false);
		if (props.pageFlowElement) {
			setDescriptionText(props.pageFlowElement.text?.textValue ? props.pageFlowElement.text.textValue : '');
			setTranslations(props.pageFlowElement.translations);
			setIsBeforeQuestions(props.pageFlowElement.sequence !== undefined ? getMaxSequence() > props.pageFlowElement.sequence : true);
		} else {
			setDescriptionText('');
			setTranslations(surveyElement.translations);
			setIsBeforeQuestions(true);
		}
	};

	useEffect(() => {
		PageFlowElementModalUtil.HandleEffect(props, setQuestionId, setQuestionError, setSurveyElement);
		handleEffect();
		return setDefaultState;
	}, [props.pageFlowElement, props.questions, props.maxId, props.maxSequence, props.isOpen]);

	const title = props.pageFlowElement
		? ContentConstants.LOGIC_AND_LAYOUT.EDIT_DESCRIPTION_TEXT
		: ContentConstants.LOGIC_AND_LAYOUT.ADD_DESCRIPTION_TEXT;

	const [descriptionTextError, setDescriptionTextError] = useState(false);
	const [descriptionText, setDescriptionText] = useState<string>(
		props.pageFlowElement?.text?.textValue ? props.pageFlowElement.text.textValue : '',
	);
	const [surveyElement, setSurveyElement] = useState<IPageFlowElementModel>(PageFlowElementModalUtil.InitializeSurveyElement(props));
	const [translations, setTranslations] = useState(props.pageFlowElement ? props.pageFlowElement.translations : surveyElement.translations);
	const [questionId, setQuestionId] = useState('');
	const [questionError, setQuestionError] = useState(false);
	const [questionList, setQuestionList] = useState(PageFlowElementModalUtil.InitializeQuestionList(props, false));
	const [isBeforeQuestions, setIsBeforeQuestions] = useState(true);
	const [radios, setRadios] = useState(InitializeRadios());

	const onTranslationChange = (translationItems: ITranslationEditItem[]) => {
		setTranslations(translationItems);
	};

	const onChange = (e: any) => {
		setDescriptionText(e);
	};

	const setDefaultState = () => {
		PageFlowElementModalUtil.SetDefaultState(props, setQuestionId, setQuestionError, setSurveyElement, setQuestionList, false);
		setDescriptionText('');
		setDescriptionTextError(false);
		setTranslations(surveyElement.translations);
		setRadios(InitializeRadios);
		setIsBeforeQuestions(true);
	};

	const isValid = () => {
		let questionValid = true;
		if (isBeforeQuestions) {
			questionValid = PageFlowElementModalUtil.IsValid(questionId, setQuestionError);
		}

		if (descriptionText === '') {
			setDescriptionTextError(descriptionText === '');
			return false;
		}

		if (!questionValid) {
			return false;
		}

		return true;
	};

	const handleSubmit = () => {
		if (isValid()) {
			if (surveyElement.text.textValue !== descriptionText) {
				surveyElement.text.editState = EditState.Update;
			}

			surveyElement.text.textValue = descriptionText ? descriptionText : '';
			surveyElement.translations = translations;

			if (!isBeforeQuestions) {
				return handleAfterLastQuestionSubmit();
			}
		}
		PageFlowElementModalUtil.HandleSubmit(props, questionId, surveyElement, setQuestionError, setSurveyElement, isValid);
	};

	const handleAfterLastQuestionSubmit = () => {
		const beforeQuestionSequence = getMaxSequence() + 1;
		const originalSequence: number | undefined = props.pageFlowElement?.sequence;
		setSurveyElement(SurveyDesignHelper.UpdateExistingElement(surveyElement, beforeQuestionSequence, originalSequence));
		props.onSubmit([surveyElement]);
	};

	const getMaxSequence = () => {
		return props.maxSequence ?? 0;
	};

	const handleClose = () => {
		PageFlowElementModalUtil.HandleClose(setDefaultState, props.onClose);
	};

	const toggleRadio = () => {
		setIsBeforeQuestions(!isBeforeQuestions);
	};

	return (
		<PageFlowElementModal {...props} title={title} onSubmit={handleSubmit} onClose={handleClose} contentFullWidth={true}>
			<PageFlowElementModalUtil.PageFlowContainer>
				<Col3Col9 label={ContentConstants.LOGIC_AND_LAYOUT.DESCRIPTION_TEXT} className='tws-description-text'>
					<RichTextEditor
						placeholder={ContentConstants.LOGIC_AND_LAYOUT.DESCRIPTION_TEXT}
						error={descriptionTextError}
						messages={[ContentConstants.LOGIC_AND_LAYOUT.DESCRIPTION_TEXT_ERROR_MESSAGE]}
						mode={'custom'}
						toolbar={Constants.customHtmlEditorOptions}
						value={descriptionText}
						onChange={onChange}
					/>
				</Col3Col9>
				{props.supportedCultures === undefined
					? 0 > 1
					: props.supportedCultures.length > 1 && (
							<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_DIMENSION_TRANSLATIONS} className='tws-description-text-translations'>
								<TranslationEditor
									enableHtmlEditor={true}
									hasDefaultTranslation={descriptionText?.trim().length > 0}
									property='IPageFlowElementModel'
									propertyDisplayName={ContentConstants.TRANSLATIONS_DESCRIPTION_TEXT}
									onChange={onTranslationChange}
									translations={translations}
									showErrors={false}
								/>
							</Col3Col9>
					  )}
				<Col3Col9 label={ContentConstants.POSITION} className='tws-position-row'>
					<PositionContainer>
						<BeforeQuestionContainer>
							<RadioButtonContainer>
								<RadioButton
									groupName={'descriptionText'}
									isChecked={isBeforeQuestions}
									key={'beforeQuestions'}
									item={radios[0]}
									handleChange={toggleRadio}
									showTooltips={true}
								/>
							</RadioButtonContainer>
							<QuestionSelectContainer>
								<PageFlowQuestionSection
									questions={questionList}
									questionError={questionError}
									questionId={questionId}
									handleQuestionChange={(value: any) =>
										PageFlowElementModalUtil.HandleQuestionChange(value, setQuestionId, setQuestionError)
									}
									disabled={!isBeforeQuestions}
								/>
							</QuestionSelectContainer>
						</BeforeQuestionContainer>
						<div>
							<RadioButton
								groupName={'descriptionText'}
								isChecked={!isBeforeQuestions}
								key={'endOfSurvey'}
								item={radios[1]}
								handleChange={toggleRadio}
								showTooltips={true}
							/>
						</div>
					</PositionContainer>
				</Col3Col9>
			</PageFlowElementModalUtil.PageFlowContainer>
		</PageFlowElementModal>
	);
};
